import { useQuery } from "@tanstack/react-query";
import { FileText, Printer, FolderOpen, Share2, LayoutGrid } from "lucide-react";
import { api, API } from "@/lib/api";

export const useDocuments = () => useQuery({ queryKey: ["documents"], queryFn: () => api.get("/documents").then((r) => r.data) });

export const VariantButton = ({ kind, doc, mode, fallbackHref, size = "md" }) => {
  const print = mode === "print";
  const href = doc ? `${API}/documents/${kind}/download${print ? "" : "?dl=1"}` : fallbackHref;
  const ready = !!href;
  const Icon = print ? Printer : Share2;
  const label = print ? "Print-friendly version" : "Download for email & socials";
  const base = size === "sm" ? "h-9 px-3 text-[11px]" : "h-10 px-4 text-xs";
  const cls = print
    ? "bg-[#FAF8F2] text-[#1B2A41] border border-[#1B2A41]/20 hover:border-[#E2705A] hover:text-[#E2705A]"
    : "bg-[#1B2A41] text-[#FDFBF7] hover:bg-[#E2705A]";
  if (!ready) {
    return (
      <span data-testid={`document-${kind}-${mode}`} aria-disabled="true" className={`inline-flex items-center gap-2 ${base} rounded-full border border-dashed border-[#D8D2C4] text-[#5C6A79] font-type font-bold uppercase tracking-wider cursor-not-allowed`}>
        <Icon className="w-4 h-4" /> {label} <span className="normal-case font-normal opacity-70">· soon</span>
      </span>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`document-${kind}-${mode}`} title={doc?.original_filename} className={`inline-flex items-center gap-2 ${base} rounded-full font-type font-bold uppercase tracking-wider transition-colors ${cls}`}>
      <Icon className="w-4 h-4" /> {label}
    </a>
  );
};

const DocTile = ({ icon: Icon, title, blurb, digitalKind, printKind, byKind, digitalFallback }) => (
  <div data-testid={`document-tile-${digitalKind}`} className="flex flex-col gap-4 p-5 sm:p-6 rounded-2xl bg-[#FAF8F2] border-2 border-[#1B2A41]/15 hover:border-[#1B2A41]/30 transition-colors">
    <div className="flex items-start gap-4">
      <span className="w-12 h-12 rounded-xl bg-[#1B2A41] text-[#FDFBF7] flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" /></span>
      <div className="min-w-0">
        <h3 className="font-semibold text-[#1B2A41] text-sm sm:text-base">{title}</h3>
        <p className="text-xs text-[#5C6A79] mt-0.5">{blurb}</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-2 mt-auto">
      <VariantButton kind={digitalKind} doc={byKind[digitalKind]} mode="digital" fallbackHref={digitalFallback} />
      <VariantButton kind={printKind} doc={byKind[printKind]} mode="print" />
    </div>
  </div>
);

export const DocumentsSection = () => {
  const { data = [] } = useDocuments();
  const { data: cards = [] } = useQuery({ queryKey: ["cards"], queryFn: () => api.get("/cards").then((r) => r.data) });
  const hasCards = cards.length > 0;
  const byKind = Object.fromEntries(data.map((d) => [d.kind, d]));
  return (
    <section id="documents" className="max-w-6xl mx-auto px-5 sm:px-8 pb-12" data-testid="documents-section">
      <div className="flex items-center gap-3 mb-5">
        <FolderOpen className="w-5 h-5 text-[#E2705A]" />
        <h2 className="font-type font-bold text-base md:text-lg text-[#1B2A41] uppercase tracking-wider">Documents</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <DocTile icon={FileText} title="The R.O.A.N framework (latest PDF)" blurb="The latest full document." digitalKind="latest_pdf" printKind="latest_pdf_print" byKind={byKind} />
        <DocTile icon={Printer} title="The ROAN flyer" blurb="One page to hand out or post." digitalKind="flyer" printKind="flyer_print" byKind={byKind} />
        <DocTile icon={LayoutGrid} title="Palm cards — the full pack" blurb="All the talking cards in one file." digitalKind="cards_digital" printKind="cards_print" byKind={byKind} digitalFallback={hasCards ? `${API}/cards/pack.pdf` : undefined} />
      </div>
    </section>
  );
};
